import React, { useState, useRef, useEffect } from "react";
import { useAtom } from "../app-jotai";
import {
  authDialogStateAtom,
  authSessionAtom,
  authStorage,
  activeCanvasAtom,
  canvasListDialogStateAtom,
  saveCanvasDialogStateAtom,
} from "./auth-state";
import { authApi } from "./auth-api";
import "./UserProfileButton.scss";

export const UserProfileButton: React.FC<{
  onNewCanvas?: () => void;
}> = ({ onNewCanvas }) => {
  const [session, setSession] = useAtom(authSessionAtom);
  const [, setActiveCanvas] = useAtom(activeCanvasAtom);
  const [, setAuthDialog] = useAtom(authDialogStateAtom);
  const [, setCanvasListDialog] = useAtom(canvasListDialogStateAtom);
  const [, setSaveCanvasDialog] = useAtom(saveCanvasDialogStateAtom);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Initial session verification with backend on startup
  useEffect(() => {
    let isMounted = true;
    const verifySession = async () => {
      if (session.accessToken) {
        try {
          const res = await authApi.getMe(session.accessToken);
          if (isMounted) {
            setSession((prev) => ({
              ...prev,
              user: {
                id: res.user.id,
                name: res.user.name,
                email: res.user.email,
                role: res.user.role,
              },
            }));
          }
        } catch {
          // Attempt refresh if access token expired
          try {
            const refreshRes = await authApi.refresh();
            if (isMounted) {
              authStorage.save(refreshRes.accessToken, refreshRes.user);
              setSession({
                user: refreshRes.user,
                accessToken: refreshRes.accessToken,
                isLoading: false,
                error: null,
              });
            }
          } catch {
            if (isMounted) {
              authStorage.clear();
              setSession({
                user: null,
                accessToken: null,
                isLoading: false,
                error: null,
              });
            }
          }
        }
      }
    };

    verifySession();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    authStorage.clear();
    setSession({
      user: null,
      accessToken: null,
      isLoading: false,
      error: null,
    });
  };

  if (!session.user) {
    return (
      <div className="UserProfileButton-container">
        <button
          type="button"
          className="UserProfileButton is-unauth"
          onClick={() => setAuthDialog({ isOpen: true, initialTab: "login" })}
          title="Sign in to sync canvases and collaborate"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          <span>Sign In</span>
        </button>
      </div>
    );
  }

  const initial = session.user.name ? session.user.name.charAt(0).toUpperCase() : "U";

  return (
    <div className="UserProfileButton-container" ref={dropdownRef}>
      <button
        type="button"
        className="UserProfileButton is-auth"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <span className="UserProfileButton__avatar">{initial}</span>
        <span className="UserProfileButton__name">{session.user.name}</span>
        {session.user.role?.toLowerCase() === "admin" && (
          <span className="UserProfileButton__badge">ADMIN</span>
        )}
        <svg
          className="UserProfileButton__arrow"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 1L5 5L9 1" />
        </svg>
      </button>

      {isDropdownOpen && (
        <div className="UserProfileDropdown">
          <div className="UserProfileDropdown__header">
            <span className="UserProfileDropdown__fullname">
              {session.user.name}
            </span>
            <span className="UserProfileDropdown__email">
              {session.user.email}
            </span>
            {session.user.role?.toLowerCase() === "admin" && (
              <span className="UserProfileDropdown__role">ADMIN</span>
            )}
          </div>

          <div className="UserProfileDropdown__separator" />

          <button
            type="button"
            className="UserProfileDropdown__item"
            onClick={() => {
              setIsDropdownOpen(false);
              setCanvasListDialog({ isOpen: true });
            }}
          >
            <span>Cloud Canvases</span>
          </button>

          <button
            type="button"
            className="UserProfileDropdown__item"
            onClick={() => {
              setIsDropdownOpen(false);
              setSaveCanvasDialog({ isOpen: true });
            }}
          >
            <span>Save to Cloud</span>
          </button>

          <button
            type="button"
            className="UserProfileDropdown__item"
            onClick={() => {
              setIsDropdownOpen(false);
              setActiveCanvas(null);
              onNewCanvas?.();
            }}
          >
            <span>New Blank Canvas</span>
          </button>

          <div className="UserProfileDropdown__separator" />

          <button
            type="button"
            className="UserProfileDropdown__item is-danger"
            onClick={handleLogout}
          >
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};
